import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { SpryAuthProvider } from "spry-apps-dropdown";
import oidcConfig from "./auth/oidcConfig";
import { OidcCallback } from "./auth/OidcCallback";

import ExcalidrawApp from "./App";

window.__EXCALIDRAW_SHA__ = import.meta.env.VITE_APP_GIT_SHA;

// Clear stale multi-account flags left behind by a previously interrupted
// add-account flow. The real guard is inside onSigninCallback; this is just
// a safety net on every normal app start.
const normalizedPathForCleanup =
  window.location.pathname.replace(/\/+$/, "") || "/";
if (normalizedPathForCleanup !== "/auth/oidc/callback") {
  try {
    window.localStorage.removeItem("spry_add_account_in_progress");
    window.localStorage.removeItem("spry_add_account_pending");
  } catch {
    // ignore storage errors
  }
}

const rootElement = document.getElementById("root")!;
const root = createRoot(rootElement);

const isCallback = window.location.pathname === "/auth/oidc/callback";
const content = isCallback ? <OidcCallback /> : <ExcalidrawApp />;

const { onSigninCallback, ...oidcSettings } = oidcConfig;

// The default matchSignoutCallback checks only if the URL *starts with*
// post_logout_redirect_uri (= window.location.origin), which matches every
// page and incorrectly strips query params on every load. We require a
// 'state' param AND the absence of a 'code' param — signin callbacks carry
// both, signout callbacks carry only 'state'.
const matchSignoutCallback = (settings: {
  post_logout_redirect_uri?: string;
  redirect_uri?: string;
}) => {
  const target = settings.post_logout_redirect_uri || settings.redirect_uri;
  if (!target || !window.location.href.startsWith(target)) return false;
  const params = new URLSearchParams(window.location.search);
  return !!params.get("state") && !params.get("code");
};

root.render(
  <StrictMode>
    <SpryAuthProvider
      config={oidcSettings}
      onSigninCallback={onSigninCallback}
      matchSignoutCallback={matchSignoutCallback}
    >
      {content}
    </SpryAuthProvider>
  </StrictMode>,
);
